import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faMicrophoneSlash } from '@fortawesome/free-solid-svg-icons';
import '../../assets/styles/SectionBand.css';
import '../../assets/styles/Home.css';
import CreateSurveyModal from '../Admin-/CreateSurvey/CreateSurveyModal';
import { useLanguage } from '../../assets/js/LanguageContext';
import { sendPinEmails } from '../../utils/pinEmails';
import SectionEyebrow from '../common/SectionEyebrow';
import ReorderList from '../common/ReorderList';
import Checkbox from '../common/Checkbox';
import Modal from '../common/Modal';
import Loader from '../common/Loader';

const HomeQuestionGenerator = () => {
  const { t, language } = useLanguage();
  const [topic, setTopic] = useState('');
  const inputRef = useRef(null);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showRecordingPopup, setShowRecordingPopup] = useState(false);
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [showMaxQuestionsPopup, setShowMaxQuestionsPopup] = useState(false);
  const [showConfirmSurveyPopup, setShowConfirmSurveyPopup] = useState(false);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [showNoQuestionsWarning, setShowNoQuestionsWarning] = useState(false);
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [hasGeneratedOnce, setHasGeneratedOnce] = useState(false);
  const [hasGeneratedMoreOnce, setHasGeneratedMoreOnce] = useState(false);
  const [surveyLimitError, setSurveyLimitError] = useState(null);
  const [createdPin, setCreatedPin] = useState(null);
  
  const navigate = useNavigate();
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const changingPlaceholders = [
    t('homeqg_placeholder_1'),
    t('homeqg_placeholder_2'),
    t('homeqg_placeholder_3'),
    t('homeqg_placeholder_4'),
    t('homeqg_placeholder_5')
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPlaceholderIndex((prevIndex) => (prevIndex + 1) % changingPlaceholders.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const parseAIQuestion = (questionString, index) => {
    questionString = questionString.trim();
    const ensureQuestionMark = (text) => (text.endsWith('?') ? text : text + '?');
    
    return {
      id: `open-${Date.now()}-${index}`,
      text: questionString,
      type: 'open',
      question: ensureQuestionMark(questionString),
      selected: false,
    };
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        uploadAudioForTranscription(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setShowRecordingPopup(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setError(t('homeqg_error_mic'));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setShowRecordingPopup(false);
    }
  };

  const uploadAudioForTranscription = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.wav');
      formData.append('lang', language);

      const response = await fetch(`${process.env.REACT_APP_AI_URL}/upload-audio`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(t('homeqg_error_transcription'));
      }

      const data = await response.json();
      
      if (data.transcription && data.transcription.trim()) {
        setTopic(prevTopic => {
          const newText = data.transcription.trim();
          if (prevTopic.trim()) {
            return `${prevTopic} ${newText}`;
          } else {
            return newText;
          }
        });
      } else if (data.message) {
        console.error('Transcription error:', data.message);
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 3000);
      } else {
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 3000);
      }
    } catch (error) {
      console.error('Error uploading audio:', error);
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 3000);
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleGenerateQuestions = async (isAdditional = false) => {
    if (!topic.trim()) {
      setShowInfoPopup(true);
      return;
    }

    if (!isAdditional && hasGeneratedOnce) {
      setShowMaxQuestionsPopup(true);
      return;
    }

    if (isAdditional && hasGeneratedMoreOnce) {
      setShowMaxQuestionsPopup(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/generate-questions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          topic: topic,
          question_types: ['abierta'],
          num_questions: 5,
          lang: language,
          // "Generar más": que no repita las que ya hay
          existing_questions: isAdditional ? suggestedQuestions.map((q) => q.text) : []
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.suggestions && Array.isArray(data.suggestions)) {
        const parsedQuestions = data.suggestions.map((question, index) => 
          parseAIQuestion(question, index)
        );
        
        if (isAdditional && suggestedQuestions.length > 0) {
          setSuggestedQuestions(prev => [...prev, ...parsedQuestions]);
          setHasGeneratedMoreOnce(true);
        } else {
          setSuggestedQuestions(parsedQuestions);
          setHasGeneratedOnce(true);
        }
      } else if (data.error) {
        setError(data.error);
      } else {
        setError(t('homeqg_error_no_questions'));
      }
    } catch (error) {
      console.error('Error generating questions:', error);
      setError(t('homeqg_error_generate'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckboxChange = (questionId) => {
    console.log('Checkbox cambiado para pregunta:', questionId);
    setSuggestedQuestions(prev => {
      const updated = prev.map(q =>
        q.id === questionId ? { ...q, selected: !q.selected } : q
      );
      console.log('Preguntas actualizadas:', updated.filter(q => q.selected).length, 'seleccionadas');
      return updated;
    });
  };

  const handleEditQuestion = (questionId, currentText) => {
    setEditingQuestionId(questionId);
    setEditingText(currentText);
  };

  const handleSaveEdit = () => {
    if (!editingText.trim()) return;

    setSuggestedQuestions(prev =>
      prev.map(q =>
        q.id === editingQuestionId 
          ? { ...q, text: editingText.trim(), question: editingText.trim() }
          : q
      )
    );
    setEditingQuestionId(null);
    setEditingText('');
  };

  const handleCancelEdit = () => {
    setEditingQuestionId(null);
    setEditingText('');
  };

  const handleDeleteQuestion = (questionId) => {
    setSuggestedQuestions(prev => prev.filter(q => q.id !== questionId));
  };

  const handleCreateSurvey = () => {
    const selectedQuestions = suggestedQuestions.filter(q => q.selected);
    console.log('Preguntas seleccionadas:', selectedQuestions.length);
    
    if (selectedQuestions.length === 0) {
      setShowNoQuestionsWarning(true);
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      localStorage.setItem('selectedQuestions', JSON.stringify(selectedQuestions));
      localStorage.setItem('surveyTopic', topic);
      navigate('/sign-up');
    } else {
      setShowConfirmSurveyPopup(true);
    }
  };

  const handleConfirmCreateSurvey = () => {
    setShowConfirmSurveyPopup(false);
    setShowSurveyModal(true);
  };

  const handleCreateSurveySubmit = async (surveyData) => {
    setIsLoading(true);
    setError(null);
    setSurveyLimitError(null);

    const token = localStorage.getItem('token');
    if (!token) {
      setError(t('homeqg_error_not_auth'));
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/encuestas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(surveyData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido del servidor' }));
        
        if (errorData.error && errorData.error.includes('Límite de encuestas alcanzado')) {
          setSurveyLimitError({
            error: errorData.error,
            detalles: errorData.detalles || null
          });
          setIsLoading(false);
          return;
        }
        
        throw new Error(errorData.error || response.statusText);
      }

      const data = await response.json();
      // Aviso por email a los participantes del Excel, igual que al crear desde plantilla o desde cero
      sendPinEmails({ emails: surveyData.participantes && surveyData.participantes.emails, pin: data.pin, language });
      setShowSurveyModal(false);
      setCreatedPin(data.pin);
    } catch (err) {
      console.error("Error al crear el diagnóstico:", err);
      setError(`${t('homeqg_error_create')}: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };



  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [topic]);

  const selectedCount = suggestedQuestions.filter((q) => q.selected).length;
  const withCount = (key) => t(key).replace('{count}', selectedCount);
  const createLabel = selectedCount === 0
    ? t('homeqg_btn_create')
    : withCount(selectedCount === 1 ? 'homeqg_btn_create_one' : 'homeqg_btn_create_count');

  return (
    <div className="section-band qg-chat">
      <div className="section-band__inner">
        <div className="section-band__head">
          <SectionEyebrow>{t('homeqg_contact_eyebrow')}</SectionEyebrow>
          <p className="section-band__lead">{t('homeqg_contact_lead')}</p>
          <ol className="qg-steps">
            {['homeqg_step1', 'homeqg_step2', 'homeqg_step3'].map((key, index) => (
              <li key={key} className="qg-steps__item">
                <span className="qg-steps__num" aria-hidden="true">{index + 1}</span>
                {t(key)}
              </li>
            ))}
          </ol>
        </div>

        <div className="chatbox">
          <div className="chatbox__frame">
            <div className="chatbox__inner">
              <textarea
                ref={inputRef}
                className="chatbox__input changing-placeholder"
                placeholder={changingPlaceholders[currentPlaceholderIndex]}
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                rows="1"
                disabled={isLoading || isRecording}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (!topic.trim()) {
                      setShowInfoPopup(true);
                    } else {
                      handleGenerateQuestions(false);
                    }
                  }
                }}
              />

              <div className="chatbox__options">
                <div className="chatbox__tools">
                  <button
                    type="button"
                    className={`chatbox__tool ${isRecording ? 'is-recording' : ''}`}
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isLoading}
                    aria-label={isRecording ? t('homeqg_recording_stop') : t('homeqg_recording_title')}
                  >
                    <FontAwesomeIcon icon={isRecording ? faMicrophoneSlash : faMicrophone} />
                  </button>
                </div>

                <button
                  type="button"
                  className="chatbox__send"
                  onClick={() => {
                    if (!topic.trim()) {
                      setShowInfoPopup(true);
                    } else {
                      handleGenerateQuestions(false);
                    }
                  }}
                  disabled={isLoading}
                  aria-label={t('homeqg_btn_send')}
                >
                  <i>
                    <svg viewBox="0 0 512 512" aria-hidden="true">
                      <path
                        fill="currentColor"
                        d="M473 39.05a24 24 0 0 0-25.5-5.46L47.47 185h-.08a24 24 0 0 0 1 45.16l.41.13l137.3 58.63a16 16 0 0 0 15.54-3.59L422 80a7.07 7.07 0 0 1 10 10L226.66 310.26a16 16 0 0 0-3.59 15.54l58.65 137.38c.06.2.12.38.19.57c3.2 9.27 11.3 15.81 21.09 16.25h1a24.63 24.63 0 0 0 23-15.46L478.39 64.62A24 24 0 0 0 473 39.05"
                      ></path>
                    </svg>
                  </i>
                </button>
              </div>
            </div>
          </div>
        </div>
        <p className="qg-hint">{t('homeqg_input_hint')}</p>

        {error && (
          <p className="qg-error" role="alert">{error}</p>
        )}

        {suggestedQuestions.length > 0 && (
          <div className="qg-results">
            <h3 className="qg-results__title">{t('homeqg_generated_title')}</h3>
            <p className="qg-results__hint">{t('homeqg_results_hint')}</p>

            <ReorderList
              items={suggestedQuestions}
              getId={(q) => q.id}
              getLabel={(q) => q.question}
              onReorder={setSuggestedQuestions}
              label={t('homeqg_generated_title')}
              gripLabel={t('homeqg_reorder')}
              renderItem={(q) => (
                <div className="qg-question">
                  <Checkbox
                    checked={q.selected}
                    onChange={() => handleCheckboxChange(q.id)}
                    hideLabel
                  >
                    {q.question}
                  </Checkbox>

                  <div className={`qg-question__body ${editingQuestionId === q.id ? 'is-editing' : ''}`}>
                    {editingQuestionId === q.id ? (
                      <>
                        <textarea
                          className="qg-question__edit"
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          placeholder={t('homeqg_edit_placeholder')}
                        />
                        <div className="qg-question__actions">
                          <button type="button" className="btn btn--primary btn--sm" onClick={handleSaveEdit}>
                            {t('homeqg_edit_save')}
                          </button>
                          <button type="button" className="btn btn--ghost btn--sm" onClick={handleCancelEdit}>
                            {t('homeqg_edit_cancel')}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="qg-question__text">{q.question}</p>
                        <div className="qg-question__actions">
                          <button
                            type="button"
                            className="btn btn--outline btn--sm"
                            onClick={() => handleEditQuestion(q.id, q.text)}
                          >
                            <Pencil size={14} strokeWidth={2} aria-hidden="true" />
                            {t('homeqg_edit_modify')}
                          </button>
                          <button
                            type="button"
                            className="btn btn--sm tp-builder__delete"
                            onClick={() => handleDeleteQuestion(q.id)}
                          >
                            <Trash2 size={14} strokeWidth={2} aria-hidden="true" />
                            {t('homeqg_delete')}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            />

            <div className="qg-results__actions">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => handleGenerateQuestions(true)}
                disabled={isLoading || !topic.trim()}
              >
                {t('homeqg_btn_generate')}
              </button>

              <button
                type="button"
                className="btn btn--primary"
                onClick={handleCreateSurvey}
                aria-disabled={selectedCount === 0}
              >
                {createLabel}
              </button>
            </div>
            {!localStorage.getItem('token') && (
              <p className="qg-results__note">{t('homeqg_guest_note')}</p>
            )}
          </div>
        )}

        {(isLoading || isTranscribing) && <Loader overlay caption={t(isTranscribing ? 'homeqg_loading_audio' : 'homeqg_loading_questions')} />}

        {showRecordingPopup && (
          <>
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                zIndex: 999,
                cursor: 'not-allowed'
              }}
              onClick={(e) => e.stopPropagation()}
            />
            
            <div style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              padding: '30px 50px',
              borderRadius: '15px',
              zIndex: 1000,
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
              animation: 'fadeIn 0.3s ease-in'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                backgroundColor: '#d9534f',
                borderRadius: '50%',
                margin: '0 auto 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 1s infinite'
              }}>
                <FontAwesomeIcon icon={faMicrophone} style={{ fontSize: '24px', color: 'white' }} />
              </div>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '20px' }}>
                {t('homeqg_recording_title')}
              </h3>
              <p style={{ margin: '0 0 20px 0', fontSize: '16px', opacity: 0.9 }}>
                {t('homeqg_recording_text')}
              </p>
              <button
                onClick={stopRecording}
                style={{
                  backgroundColor: '#d9534f',
                  color: 'white',
                  border: 'none',
                  padding: '12px 30px',
                  borderRadius: '25px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#c9302c';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#d9534f';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
                }}
              >
                {t('homeqg_recording_stop')}
              </button>
            </div>
          </>
        )}

        {showErrorPopup && (
          <>
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                zIndex: 999,
                cursor: 'pointer'
              }}
              onClick={() => setShowErrorPopup(false)}
            />
            
            <div style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              padding: '30px 50px',
              borderRadius: '15px',
              zIndex: 1000,
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
              animation: 'fadeIn 0.3s ease-in'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                backgroundColor: '#ff6b6b',
                borderRadius: '50%',
                margin: '0 auto 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 1s infinite'
              }}>
                <span style={{ fontSize: '24px', color: 'white' }}>⚠️</span>
              </div>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '20px' }}>
                {t('homeqg_error_title')}
              </h3>
              <p style={{ margin: '0 0 20px 0', fontSize: '16px', opacity: 0.9 }}>
                {t('homeqg_error_text')}
              </p>
              <button
                onClick={() => setShowErrorPopup(false)}
                style={{
                  backgroundColor: '#ff6b6b',
                  color: 'white',
                  border: 'none',
                  padding: '12px 30px',
                  borderRadius: '25px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#ff5252';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#ff6b6b';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
                }}
              >
                {t('homeqg_btn_understood')}
              </button>
            </div>
          </>
        )}

        <Modal
          open={showInfoPopup}
          onOpenChange={setShowInfoPopup}
          variant="info"
          title={t('homeqg_info_title')}
          description={t('homeqg_info_text')}
          confirmText={t('homeqg_btn_understood')}
        />

        <Modal
          open={showMaxQuestionsPopup}
          onOpenChange={setShowMaxQuestionsPopup}
          variant="warning"
          title={t('homeqg_max_title')}
          description={t('homeqg_max_text')}
          confirmText={t('homeqg_btn_understood')}
        />

        <Modal
          open={showConfirmSurveyPopup}
          onOpenChange={setShowConfirmSurveyPopup}
          variant="confirm"
          title={withCount(selectedCount === 1 ? 'homeqg_confirm_title_one' : 'homeqg_confirm_title')}
          description={t('homeqg_confirm_text')}
          cancelText={t('homeqg_confirm_cancel')}
          confirmText={t('homeqg_confirm_create')}
          onConfirm={handleConfirmCreateSurvey}
        />

        {showSurveyModal && (
          <CreateSurveyModal
            onClose={() => setShowSurveyModal(false)}
            onSubmit={handleCreateSurveySubmit}
            selectedQuestions={suggestedQuestions.filter(q => q.selected)}
            limitError={surveyLimitError}
          />
        )}

        <Modal
          open={Boolean(createdPin)}
          onOpenChange={(open) => { if (!open) { setCreatedPin(null); navigate('/dashboard'); } }}
          variant="confirm"
          title={t('homeqg_success_created')}
          description={`${t('dash_th_pin')}: ${createdPin}`}
          confirmText={t('homeqg_btn_understood')}
          onConfirm={() => { setCreatedPin(null); navigate('/dashboard'); }}
        />

        <Modal
          open={showNoQuestionsWarning}
          onOpenChange={setShowNoQuestionsWarning}
          variant="warning"
          title={t('homeqg_warning_no_selected')}
          description={t('homeqg_warning_select')}
          confirmText={t('homeqg_btn_understood')}
        />
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0% { opacity: 1; transform: scale(1); }
          100% { opacity: 0.5; transform: scale(0.9); }
        }
        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(-20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInOut {
          0%, 100% { opacity: 0; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default HomeQuestionGenerator;
